import { AxiosRequestConfig } from 'axios';
import { CreateTaskInput, UpdateTaskInput, TaskQueryParams } from '../../types';
import api from './client';

// Mirrors backend CloneOptions (taskArchiveController.ts) — same shape
// CloneTaskModal.tsx's single-task clone already sends via taskAPI.copy().
export interface BulkCloneOptions {
  cloneTitle?: boolean;
  cloneDetails?: boolean;
  cloneRequirements?: boolean;
  cloneAssignees?: boolean;
  cloneSchedule?: boolean;
  clonePriority?: boolean;
  clonePrivacy?: boolean;
  cloneReviewRequirement?: boolean;
  cloneDueTime?: boolean;
  cloneRitualSettings?: boolean;
  cloneGameSettings?: boolean;
}

// Task API
export const taskAPI = {
  create: (taskData: CreateTaskInput) => api.post('/tasks', taskData),

  getAll: (organizationId: string, params?: TaskQueryParams) =>
    api.get(`/tasks/organization/${organizationId}`, { params }),

  getById: (taskId: string) => api.get(`/tasks/${taskId}`),

  /**
   * Get complete task context for TaskDetailPage in a single call.
   * Replaces: getById() + getComments() + getMembers() + getInventoryRequirements()
   */
  getFullContext: (taskId: string, config?: AxiosRequestConfig) => api.get(`/tasks/${taskId}/full`, config),

  /**
   * Get context for CreateTaskPage in a single call.
   * Replaces: getMembers() + getSettings() + getInventoryItems() + getTemplates()
   */
  getCreateContext: (organizationId: string) =>
    api.get(`/tasks/create-context/${organizationId}`),

  update: (taskId: string, updates: UpdateTaskInput) =>
    api.put(`/tasks/${taskId}`, updates),

  delete: (taskId: string, deleteFiles?: boolean) =>
    api.delete(`/tasks/${taskId}`, { data: { deleteFiles } }),

  updateRequirement: (requirementId: number, completed: boolean) =>
    api.put(`/tasks/requirements/${requirementId}`, { completed }),

  // Spec 033: Manual completion for deferred requirements
  markRequirementComplete: (taskId: string, requirementId: number) =>
    api.post(`/tasks/${taskId}/requirements/${requirementId}/complete`),

  addCompletion: (
    taskId: string,
    data: FormData | Record<string, unknown>,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ) =>
    // FormData: axios auto-sets multipart Content-Type with boundary
    // Plain object: axios sends as JSON (more reliable for text-only completions)
    api.post(`/tasks/${taskId}/completions`, data, {
      onUploadProgress,
    }),

  getCompletions: (taskId: string) =>
    api.get(`/tasks/${taskId}/completions`),

  deleteCompletion: (completionId: number) =>
    api.delete(`/tasks/completions/${completionId}`),

  submit: (taskId: string) =>
    api.post(`/tasks/${taskId}/submit`),

  review: (taskId: string, action: 'approved' | 'rejected', comments?: string) =>
    api.post(`/tasks/${taskId}/review`, { action, comments }),

  getAuditLogs: (taskId: string) =>
    api.get(`/tasks/${taskId}/audit-logs`),

  // Spec 302 — Stalled Tasks widget "Nudge" action
  nudge: (taskId: string, assigneeUserId: string) =>
    api.post(`/tasks/${taskId}/nudge`, { assigneeUserId }),

  // Task copy/clone and archive
  copy: (taskId: string, data?: {
    title?: string;
    endDate?: string;
    assignedUserIds?: string[];
    cloneOptions?: {
      cloneTitle?: boolean;
      cloneDetails?: boolean;
      cloneRequirements?: boolean;
      cloneAssignees?: boolean;
      cloneSchedule?: boolean;
      clonePriority?: boolean;
      clonePrivacy?: boolean;
      cloneReviewRequirement?: boolean;
      cloneDueTime?: boolean;
      cloneRitualSettings?: boolean;
    };
  }) =>
    api.post(`/tasks/${taskId}/copy`, data),

  archive: (taskId: string) =>
    api.post(`/tasks/${taskId}/archive`),

  unarchive: (taskId: string) =>
    api.post(`/tasks/${taskId}/unarchive`),

  getArchived: (organizationId: string) =>
    api.get(`/tasks/organization/${organizationId}/archived`),

  // Recurring task preview
  getNextOccurrences: (taskId: string, count?: number, config?: AxiosRequestConfig) =>
    api.get(`/tasks/${taskId}/next-occurrences`, { params: { count: count || 5 }, ...config }),

  // Processing job status (for video compression progress)
  getProcessingJobStatus: (jobId: string) =>
    api.get(`/processing/jobs/${jobId}`),

  // Bulk operations
  bulkArchive: (taskIds: string[]) =>
    api.post('/tasks/bulk/archive', { taskIds }),

  bulkDelete: (taskIds: string[], deleteFiles?: boolean) =>
    api.post('/tasks/bulk/delete', { taskIds, deleteFiles }),

  bulkExtend: (taskIds: string[], newEndDate: string) =>
    api.post('/tasks/bulk/extend', { taskIds, newEndDate }),

  bulkClone: (taskIds: string[], options?: {
    titles?: { taskId: string; title: string }[];
    dueDateMode?: 'none' | 'keep' | 'shift' | 'date';
    shiftDays?: number;
    fixedDate?: string;
    cloneOptions?: BulkCloneOptions;
  }) =>
    api.post('/tasks/bulk/clone', { taskIds, ...options }),

  // Task dependencies (082-B)
  getDependency: (taskId: string) =>
    api.get(`/tasks/${taskId}/dependency`),

  setDependency: (taskId: string, dependsOnTaskId: string) =>
    api.post(`/tasks/${taskId}/dependency`, { dependsOnTaskId }),

  removeDependency: (taskId: string) =>
    api.delete(`/tasks/${taskId}/dependency`),

  getDependents: (taskId: string) =>
    api.get(`/tasks/${taskId}/dependents`),

  // Task inventory requirements
  addInventoryRequirement: (taskId: string | number, data: { inventoryItemId: number; requirementId?: number; notes?: string }) =>
    api.post(`/tasks/${taskId}/inventory-requirements`, data),

  getInventoryRequirements: (taskId: string | number) =>
    api.get(`/tasks/${taskId}/inventory-requirements`),

  markInventoryPrepared: (taskId: string | number, requirementId: number, notes?: string) =>
    api.put(`/tasks/${taskId}/inventory-requirements/${requirementId}/prepared`, { notes }),

  unmarkInventoryPrepared: (taskId: string | number, requirementId: number) =>
    api.put(`/tasks/${taskId}/inventory-requirements/${requirementId}/unprepared`),

  removeInventoryRequirement: (taskId: string | number, requirementId: number) =>
    api.delete(`/tasks/${taskId}/inventory-requirements/${requirementId}`),
};

// Comment API
export const commentAPI = {
  getComments: (taskId: string) =>
    api.get(`/tasks/${taskId}/comments`),

  addComment: (taskId: string, content: string, parentId?: number) =>
    api.post(`/tasks/${taskId}/comments`, { content, parentId }),

  updateComment: (commentId: number, content: string) =>
    api.put(`/comments/${commentId}`, { content }),

  deleteComment: (commentId: number) =>
    api.delete(`/comments/${commentId}`),
};

// Task Template API - Reusable task templates
export const taskTemplateAPI = {
  // Get all templates (with optional category filter)
  getAll: (category?: string) =>
    api.get('/task-templates', { params: category ? { category } : undefined }),

  // Get available categories
  getCategories: () => api.get('/task-templates/categories'),

  // Get single template by ID
  getById: (templateId: number) => api.get(`/task-templates/${templateId}`),

  // Create custom template
  create: (data: {
    name: string;
    description?: string;
    category?: string;
    icon?: string;
    defaultTitle?: string;
    defaultDetails?: string;
    defaultRequirements?: { description: string; type?: string; isOptional?: boolean; displayOrder?: number }[];
    defaultScheduleType?: 'one_time' | 'daily' | 'weekly' | 'monthly';
    defaultScheduleFrequency?: number;
    defaultScheduleDaysOfWeek?: number[];
    defaultScheduleDayOfMonth?: number;
    defaultPriority?: 'low' | 'medium' | 'high';
    defaultDueTime?: string;
    requireReview?: boolean;
    defaultIsPrivate?: boolean;
    isRitual?: boolean;
    ritualTimeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night' | 'anytime';
    ritualWindowMinutes?: number;
    completionPoints?: number;
    earlyBonusPoints?: number;
  }) => api.post('/task-templates', data),

  // Update existing template
  update: (templateId: number, data: {
    name?: string;
    description?: string;
    category?: string;
    icon?: string;
    defaultTitle?: string;
    defaultDetails?: string;
    defaultRequirements?: { description: string; type?: string; isOptional?: boolean; displayOrder?: number }[];
    defaultScheduleType?: 'one_time' | 'daily' | 'weekly' | 'monthly';
    defaultScheduleFrequency?: number;
    defaultScheduleDaysOfWeek?: number[];
    defaultScheduleDayOfMonth?: number;
    defaultPriority?: 'low' | 'medium' | 'high';
    defaultDueTime?: string;
    requireReview?: boolean;
    defaultIsPrivate?: boolean;
    isRitual?: boolean;
    ritualTimeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night' | 'anytime';
    ritualWindowMinutes?: number;
    completionPoints?: number;
    earlyBonusPoints?: number;
    isActive?: boolean;
  }) => api.put(`/task-templates/${templateId}`, data),

  // Delete template
  delete: (templateId: number) => api.delete(`/task-templates/${templateId}`),

  // Create task from template (instantiate)
  instantiate: (templateId: number, options: {
    title?: string;
    details?: string;
    assignedUserIds?: number[];
    startDate?: string;
    endDate?: string;
    dueTime?: string;
    priority?: 'low' | 'medium' | 'high';
    isPrivate?: boolean;
    requireReview?: boolean;
  }) => api.post(`/task-templates/${templateId}/instantiate`, options),

  // Save existing task as template
  saveTaskAsTemplate: (taskId: string, data: {
    name: string;
    description?: string;
    category?: string;
  }) => api.post(`/task-templates/from-task/${taskId}`, data),
};

// Task Game API - Dice roll, coin flip games
export const gameAPI = {
  // Get game configuration for a task
  getConfig: (taskId: string) =>
    api.get(`/games/task/${taskId}/config`),

  // Get game configuration with task info
  getConfigWithTask: (taskId: string) =>
    api.get(`/games/task/${taskId}/config-with-task`),

  // Create or update dice game configuration
  createDiceGame: (taskId: string, data: {
    diceConfig: {
      die1: Record<string, string>;
      die2: Record<string, string>;
      die3?: Record<string, string>;
    };
    allowRerollOnSix?: boolean;
    isRequired?: boolean;
    outcomeIsRequirement?: boolean;
    showResultsToAssignee?: boolean;
    showResultsToCreator?: boolean;
    showOutcomesBeforePlay?: boolean;
  }) => api.post(`/games/task/${taskId}/dice`, data),

  // Create or update coin flip configuration
  createCoinFlipGame: (taskId: string, data: {
    coinConfig: {
      heads: string;
      tails: string;
    };
    isRequired?: boolean;
    outcomeIsRequirement?: boolean;
    showResultsToAssignee?: boolean;
    showResultsToCreator?: boolean;
    showOutcomesBeforePlay?: boolean;
  }) => api.post(`/games/task/${taskId}/coin`, data),

  // Create or update spinner game configuration
  createSpinnerGame: (taskId: string, data: {
    spinnerConfig: {
      segments: { label: string; color?: string }[];
    };
    isRequired?: boolean;
    outcomeIsRequirement?: boolean;
    showResultsToAssignee?: boolean;
    showResultsToCreator?: boolean;
    showOutcomesBeforePlay?: boolean;
  }) => api.post(`/games/task/${taskId}/spinner`, data),

  // Delete game configuration
  deleteConfig: (taskId: string) =>
    api.delete(`/games/task/${taskId}/config`),

  // Get all game results for a task
  getResults: (taskId: string) =>
    api.get(`/games/task/${taskId}/results`),

  // Get latest game result for a task
  getLatestResult: (taskId: string) =>
    api.get(`/games/task/${taskId}/latest-result`),

  // Check if current user has played the game
  hasPlayed: (taskId: string) =>
    api.get(`/games/task/${taskId}/has-played`),

  // Record a dice roll. SECURITY: the outcome is generated server-side
  // (bug-registry.md "client-authoritative dice/coin/spinner outcomes") --
  // this call no longer sends a client-chosen outcome, only the wasReroll
  // metadata flag. The response carries the authoritative dice values.
  rollDice: (taskId: string, wasReroll?: boolean) =>
    api.post(`/games/task/${taskId}/roll-dice`, { wasReroll }),

  // Record a coin flip. SECURITY: see rollDice above -- outcome is
  // server-generated, response carries the authoritative result.
  flipCoin: (taskId: string) =>
    api.post(`/games/task/${taskId}/flip-coin`, {}),

  // Get rolled requirement (if outcome_is_requirement is enabled)
  getRolledRequirement: (taskId: string) =>
    api.get(`/games/task/${taskId}/rolled-requirement`),

  // Check if task can be completed (game requirements satisfied)
  canComplete: (taskId: string) =>
    api.get(`/games/task/${taskId}/can-complete`),

  // Game State Management (visual persistence)
  // Get current game state
  getGameState: (taskId: string) =>
    api.get(`/games/task/${taskId}/game`),

  // Get full game state with participants and role mappings
  getFullGameState: (taskId: string) =>
    api.get(`/games/task/${taskId}/game/full`),

  // Save played state (persists game result)
  savePlayedState: (taskId: string, result: Record<string, unknown>) =>
    api.post(`/games/task/${taskId}/game/play`, { result }),

  // Accept and lock game state
  acceptGameState: (taskId: string) =>
    api.post(`/games/task/${taskId}/game/accept`),

  // ============ POP GAME API ============

  // Create or update pop game configuration
  createPopGame: (taskId: string, data: {
    popConfig: {
      minIntervalMinutes: number;
      maxIntervalMinutes: number;
      deadlineMinutes: number;
      activeHoursStart: string;
      activeHoursEnd: string;
      activeDays: number[];
      maxDailyPops: number;
    };
    outcomeIsRequirement?: boolean;
  }) => api.post(`/games/task/${taskId}/pop`, data),

  // Get pop game configuration
  getPopConfig: (taskId: string) =>
    api.get(`/games/task/${taskId}/pop/config`),

  // Update pop game configuration
  updatePopConfig: (taskId: string, data: {
    popConfig: {
      minIntervalMinutes?: number;
      maxIntervalMinutes?: number;
      deadlineMinutes?: number;
      activeHoursStart?: string;
      activeHoursEnd?: string;
      activeDays?: number[];
      maxDailyPops?: number;
    };
  }) => api.put(`/games/task/${taskId}/pop/config`, data),

  // Get active pops for a task
  getActivePops: (taskId: string) =>
    api.get(`/games/task/${taskId}/pop/active`),

  // Get pop history for a task
  getPopHistory: (taskId: string, limit?: number, offset?: number) =>
    api.get(`/games/task/${taskId}/pop/history`, { params: { limit, offset } }),

  // Complete a pop for current user (requires task public ID and pop instance ID)
  completePop: (taskPublicId: string, popInstanceId: number) =>
    api.post(`/games/task/${taskPublicId}/pop/${popInstanceId}/complete`),

  // Get scheduling status for a task's pop
  getPopSchedulingStatus: (taskId: string) =>
    api.get(`/games/task/${taskId}/pop/status`),

  // Get user's pending pops across all tasks
  getMyPendingPops: () =>
    api.get('/games/pop/pending'),

  // Get pop analytics (Spec 092)
  getPopAnalytics: () =>
    api.get('/games/pop/analytics'),

  // Delete pop game configuration
  deletePopConfig: (taskId: string) =>
    api.delete(`/games/task/${taskId}/pop/config`),

  // Reset game state (non-binding games only)
  resetGameState: (taskId: string) =>
    api.post(`/games/task/${taskId}/game/reset`),

  // Add game participant
  addParticipant: (taskId: string, data: {
    roleKey: string;
    participantUserId?: number;
    participantName?: string;
    displayLabel?: string;
  }) => api.post(`/games/task/${taskId}/game/participants`, data),

  // Get all participants for a game
  getParticipants: (taskId: string) =>
    api.get(`/games/task/${taskId}/game/participants`),

  // Add role mapping (outcome -> participant assignment)
  addRoleMapping: (taskId: string, data: {
    outcome: string;
    roleKey: string;
    mapsToParticipant: string;
  }) => api.post(`/games/task/${taskId}/game/role-mappings`, data),

  // Get all role mappings for a game
  getRoleMappings: (taskId: string) =>
    api.get(`/games/task/${taskId}/game/role-mappings`),
};

// Reward API
export const rewardAPI = {
  // List rewards for a task
  listTaskRewards: (taskId: string) =>
    api.get(`/tasks/${taskId}/rewards`),

  // Create an immediate reward (task must be completed)
  createReward: (
    taskId: string,
    formData: FormData,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ) => api.post(`/tasks/${taskId}/rewards`, formData, { onUploadProgress }),

  // Create an incentive reward (task not yet completed)
  // Note: Uses the same endpoint as immediate rewards - formData should include rewardType='incentive'
  createIncentiveReward: (
    taskId: string,
    formData: FormData,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ) => api.post(`/tasks/${taskId}/rewards`, formData, { onUploadProgress }),

  // Get a single reward
  getReward: (rewardId: number) =>
    api.get(`/rewards/${rewardId}`),

  // View reward content (record view and get signed URL)
  viewReward: (rewardId: number) =>
    api.get(`/rewards/${rewardId}/view`),

  // Download reward (if downloadable)
  downloadReward: (rewardId: number) =>
    api.get(`/rewards/${rewardId}/download`),

  // Revoke a reward (sender only)
  revokeReward: (rewardId: number) =>
    api.post(`/rewards/${rewardId}/revoke`),

  // Delete a reward (sender only)
  deleteReward: (rewardId: number) =>
    api.delete(`/rewards/${rewardId}`),

  // Get view history for a reward (sender only - audit trail)
  getRewardViews: (rewardId: number) =>
    api.get(`/rewards/${rewardId}/views`),

  // Get user's rewards as recipient
  getMyRewards: () =>
    api.get(`/rewards/my`),

  // Get reward stats for current user
  getRewardStats: () =>
    api.get(`/rewards/stats`),
};

// Consequence API - Task consequences and punishments
export const consequenceAPI = {
  // Consequences
  getTaskConsequences: (taskId: number) =>
    api.get(`/consequences/task/${taskId}`),
  getMyActive: () => api.get('/consequences/my-active'),
  getMyHistory: () => api.get('/consequences/my-history'),
  getStats: () => api.get('/consequences/stats'),
  create: (data: {
    taskId: number;
    consequenceType?: string;
    triggerType?: 'manual' | 'task_failed' | 'requirement_missed';
    triggerRequirementId?: number;
    title: string;
    description?: string;
    consequenceCategory: 'text_task' | 'restriction' | 'media_required';
    textTaskType?: string;
    textTaskCount?: number;
    textTaskContent?: string;
    restrictionType?: string;
    restrictionDurationHours?: number;
    mediaRequired?: boolean;
    mediaInstructions?: string;
    severity?: number;
    pointsDeduction?: number;
  }) => api.post('/consequences', data),
  createFromTemplate: (taskId: string | number, templateId: number, triggerType?: string) =>
    api.post('/consequences/from-template', { taskId, templateId, triggerType }),
  triggerForTask: (taskId: number, triggerType?: string) =>
    api.post(`/consequences/trigger/${taskId}`, { triggerType }),
  acknowledge: (id: number) => api.post(`/consequences/${id}/acknowledge`),
  submit: (id: number, data: { text?: string; notes?: string }, files?: File[]) => {
    if (files && files.length > 0) {
      const formData = new FormData();
      if (data.text) formData.append('text', data.text);
      if (data.notes) formData.append('notes', data.notes);
      files.forEach(file => formData.append('files', file));
      return api.post(`/consequences/${id}/submit`, formData);
    }
    return api.post(`/consequences/${id}/submit`, data);
  },
  getFiles: (id: number) => api.get(`/consequences/${id}/files`),
  verify: (id: number, approved: boolean, notes?: string) =>
    api.post(`/consequences/${id}/verify`, { approved, notes }),
  dismiss: (id: number, reason?: string) =>
    api.post(`/consequences/${id}/dismiss`, { reason }),
  delete: (id: number) => api.delete(`/consequences/${id}`),

  // Templates
  getTemplates: () => api.get('/consequences/templates'),
  createTemplate: (data: {
    title: string;
    description?: string;
    consequenceCategory: 'text_task' | 'restriction' | 'media_required';
    textTaskType?: string;
    textTaskCount?: number;
    textTaskContent?: string;
    restrictionType?: string;
    restrictionDurationHours?: number;
    mediaRequired?: boolean;
    mediaInstructions?: string;
    severity?: number;
    pointsDeduction?: number;
    purpose?: 'corrective' | 'funishment' | 'maintenance';
    automationConfig?: {
      autoTrigger: boolean;
      triggerType: 'review_rejected' | 'streak_break' | 'pattern';
      patternConfig?: { missCount: number; windowDays: number };
      requireConfirmation: boolean;
    } | null;
  }) => api.post('/consequences/templates', data),
  updateTemplate: (id: number, data: Partial<{
    title: string;
    description: string;
    consequenceCategory: string;
    textTaskType: string;
    textTaskCount: number;
    textTaskContent: string;
    restrictionType: string;
    restrictionDurationHours: number;
    mediaRequired: boolean;
    mediaInstructions: string;
    severity: number;
    pointsDeduction: number;
    automationConfig: {
      autoTrigger: boolean;
      triggerType: 'review_rejected' | 'streak_break' | 'pattern';
      patternConfig?: { missCount: number; windowDays: number };
      requireConfirmation: boolean;
    } | null;
  }>) => api.put(`/consequences/templates/${id}`, data),
  deleteTemplate: (id: number) => api.delete(`/consequences/templates/${id}`),
};
